import { useRouter } from 'expo-router';
import { AlertTriangle, X } from 'lucide-react-native';
import { useState } from 'react';

import { useAskConversationContext } from './LibraryChatContext';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  useDeleteAskConversation,
  useRenameAskConversation,
} from '@/hooks/ask/useManageAskConversation';
import type { AskErrorKind } from '@/services/ask/errors';

const TITLE_MAX_LENGTH = 200;

function renameErrorMessage(kind: AskErrorKind): string {
  if (kind === 'LIKELY_IDENTIFIER') {
    return 'This title looks like it may include a patient identifier. Remove it and save again.';
  }

  return "The title couldn't be saved. Try again.";
}

/**
 * Renames the open conversation. Patient identifiers are screened by the
 * server alone; a title it refuses stays in the field with the reason below it.
 */
function RenameModal({
  conversationId,
  initialTitle,
  onClose,
}: {
  conversationId: string;
  initialTitle: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(initialTitle);

  const rename = useRenameAskConversation(conversationId);

  const trimmed = title.trim();

  const save = () => {
    rename.mutate({ title: trimmed }, { onSuccess: onClose });
  };

  /*
   * A refusal speaks only while the field still holds the title it refused,
   * so editing the title clears it.
   */
  const errorMessage =
    rename.isError && rename.variables?.title === trimmed
      ? renameErrorMessage(rename.error.kind)
      : null;

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalBackdrop />

      <ModalContent className="p-5">
        <ModalHeader className="pb-3">
          <Heading size="md" className="text-typography-900">
            Rename topic
          </Heading>

          <ModalCloseButton onPress={onClose} accessibilityLabel="Close">
            <Icon as={X} className="text-typography-700" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody className="pb-4">
          <VStack className="gap-2">
            <Input className="border-outline-500">
              <InputField
                aria-label="Topic title"
                value={title}
                onChangeText={setTitle}
                maxLength={TITLE_MAX_LENGTH}
                autoFocus
              />
            </Input>

            {errorMessage ? (
              <HStack className="items-start gap-2" accessibilityLiveRegion="polite">
                <Icon as={AlertTriangle} className="mt-0.5 h-4 w-4 text-error-600" />

                <Text className="flex-1 text-sm text-typography-900">{errorMessage}</Text>
              </HStack>
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter className="flex-row justify-end gap-2">
          <Button variant="outline" action="secondary" onPress={onClose}>
            <ButtonText>Cancel</ButtonText>
          </Button>

          <Button
            action="primary"
            isDisabled={trimmed.length === 0 || rename.isPending}
            onPress={save}>
            <ButtonText>Save</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DeleteModal({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const router = useRouter();

  const remove = useDeleteAskConversation(conversationId);

  const confirm = () => {
    remove.mutate(undefined, {
      onSuccess: () => {
        onClose();

        router.replace(ROUTES.LIBRARY_CHAT);
      },
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalBackdrop />

      <ModalContent className="p-5">
        <ModalHeader className="pb-3">
          <Heading size="md" className="text-typography-900">
            Delete this research topic?
          </Heading>
        </ModalHeader>

        <ModalBody className="pb-4">
          <VStack className="gap-2">
            <Text className="text-sm text-typography-900">
              It will be removed from your history on every device.
            </Text>

            {remove.isError ? (
              <Text className="text-sm text-typography-900" accessibilityLiveRegion="polite">
                The topic couldn't be deleted. Try again.
              </Text>
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter className="flex-row justify-end gap-2">
          <Button variant="outline" action="secondary" onPress={onClose}>
            <ButtonText>Cancel</ButtonText>
          </Button>

          <Button action="negative" isDisabled={remove.isPending} onPress={confirm}>
            <ButtonText>Delete</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export type AskConversationDialogKind = 'rename' | 'delete';

/**
 * The open conversation's title, and whether it can be renamed or deleted. A
 * new conversation has no title or server record yet, so it can be neither,
 * and the header names the feature instead. The server names a topic after
 * its first turn, so an untitled saved topic is still being named and reads as
 * a new topic rather than "Untitled". A saved topic that has not loaded, or
 * failed to, has no title yet, so the bar stays blank rather than misnaming it.
 */
export function useAskConversationTitle() {
  const { isNew, snapshot } = useAskConversationContext();

  const savedTitle = snapshot ? (snapshot.title ?? 'New research topic') : '';

  const title = isNew ? 'Ask Primum' : savedTitle;

  const canManage = !isNew && snapshot != null;

  return { title, isTopicTitle: !isNew, canManage };
}

/** The rename or delete dialog for the open conversation, whichever is asked for. */
export function AskConversationDialog({
  dialog,
  onClose,
}: {
  dialog: AskConversationDialogKind | null;
  onClose: () => void;
}) {
  const { conversationId, snapshot } = useAskConversationContext();

  if (dialog === 'rename') {
    return (
      <RenameModal
        conversationId={conversationId}
        initialTitle={snapshot?.title ?? ''}
        onClose={onClose}
      />
    );
  }

  if (dialog === 'delete') {
    return <DeleteModal conversationId={conversationId} onClose={onClose} />;
  }

  return null;
}
